import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, withModifiers as _withModifiers, withKeys as _withKeys, withCtx as _withCtx } from "vue"

const _hoisted_1 = ["id"]

import {
    ref,
    toRefs,
    computed,
} from 'vue'
import { useFocus } from '@vueuse/core';
import { QInput } from 'quasar';

// Personal libs
import { $t } from 'src/i18n/utils';
import { checkPassword } from '@shared/lib/security.lib';
import {
    passwordRules,
    passwordExample
} from '@shared/lib/form.lib';
import { PasswordTypeEnum } from '@shared/enum/password-type.enum';

/**
 * HTMLElement refs
 */
interface QInputEmailProps {
    // Model
    modelValue?: string

    // Content
    label: string
    placeholder?: string
    ariaDescribedBy?: string
    showAriaDescription?: boolean
    passwordType?: PasswordTypeEnum

    // Style
    extraClass?: string;
    dense?: boolean

    // State
    disable?: boolean
    required?: boolean

    // Actions
    onEnterKey?: (...args: any[]) => void;
}

export default /*#__PURE__*/_defineComponent({
  __name: 'QInputPassword',
  props: {
    modelValue: {},
    label: {},
    placeholder: { default: undefined },
    ariaDescribedBy: {},
    showAriaDescription: { type: Boolean, default: false },
    passwordType: { default: PasswordTypeEnum.CURRENT },
    extraClass: {},
    dense: { type: Boolean },
    disable: { type: Boolean, default: false },
    required: { type: Boolean, default: true },
    onEnterKey: { type: Function, default: undefined }
  },
  emits: ['update:modelValue'],
  setup(__props: any, { expose: __expose, emit: __emit }) {

// Vendor
const customQInputPassword = ref<QInput>();
const { focused: customQInputPasswordFocused } = useFocus(customQInputPassword);

/**
 * Properties
 */
const props = __props;
const {
    onEnterKey,
    required,
    label,
    placeholder,
    ariaDescribedBy,
    extraClass,
    disable,
    modelValue,
    passwordType
} = toRefs(props);

/**
 * Events
 */
const emit = __emit

/**
 * Data
 */
const isPwd = ref<boolean | undefined>(true);

/**
 * Computed properties
 */
// If required, add a star at the beginning of the label
const computedLabel = computed<string>(() => `${required.value ? '* ' : ''}${label.value}`)
const computedResetLabel = computed<string>(() => {
    return $t(`form.password.${PasswordTypeEnum.valueOf(passwordType.value) ?? PasswordTypeEnum.NORMAL}.reset`)
})
const computedShowLabel = computed<string>(() => {
    return $t(`form.password.${PasswordTypeEnum.valueOf(passwordType.value) ?? PasswordTypeEnum.NORMAL}.show`)
})
const computedHideLabel = computed<string>(() => {
    return $t(`form.password.${PasswordTypeEnum.valueOf(passwordType.value) ?? PasswordTypeEnum.NORMAL}.hide`)
})

/**
 * Methods
 */
function clearPassword() {
    customQInputPassword.value?.resetValidation();
    emit('update:modelValue', undefined)
    customQInputPasswordFocused.value = true;
}

function hasError() {
    return customQInputPassword.value?.hasError
}

function validate() {
    customQInputPassword.value?.validate()
}

function resetValidation() {
    customQInputPassword.value?.resetValidation()
}

/**
 * Exposed methods
 */
__expose({
    clearPassword,
    hasError,
    validate,
    resetValidation
})

return (_ctx: any,_cache: any) => {
  const _component_q_icon = _resolveComponent("q-icon")!
  const _component_q_btn = _resolveComponent("q-btn")!

  return (_openBlock(), _createElementBlock("div", null, [
    (_unref(ariaDescribedBy) == null)
      ? (_openBlock(), _createElementBlock("span", {
          key: 0,
          class: _normalizeClass({
                'q-mx-sm': true,
                'sr-only': _ctx.showAriaDescription === false
            }),
          id: _unref(ariaDescribedBy) ?? 'id_example_password'
        }, " Exemple de mot de passe : " + _toDisplayString(_unref(passwordExample)), 11 /* TEXT, CLASS, PROPS */, _hoisted_1))
      : _createCommentVNode("v-if", true),
    _createVNode(_unref(QInput), {
      "model-value": _unref(modelValue),
      "onUpdate:modelValue": _cache[2] || (_cache[2] = (newValue) => emit('update:modelValue', newValue)),
      ref_key: "customQInputPassword",
      ref: customQInputPassword,
      dense: _ctx.dense,
      placeholder: _unref(placeholder),
      "aria-required": _unref(required),
      "aria-describedby": _unref(ariaDescribedBy) ?? 'id_example_password',
      "aria-invalid": _unref(checkPassword)(_unref(modelValue) ?? '') ? 'false' : 'true',
      "aria-disable": _unref(disable),
      disable: _unref(disable),
      label: computedLabel.value,
      type: isPwd.value ? 'password' : 'text',
      outlined: "",
      autocomplete: "current-password",
      "bg-color": _ctx.$q.dark.isActive ? 'black' : 'white',
      "label-color": _ctx.$q.dark.isActive ? 'white' : 'black',
      class: _normalizeClass(['q-mb-sm', _unref(extraClass)]),
      "input-class": [
                _ctx.$q.dark.isActive ? 'text-white' : 'text-black',
                'set-outline',
            ],
      "hide-bottom-space": "",
      "lazy-rules": "",
      rules: _unref(passwordRules),
      enterkeyhint: "done",
      onKeyup: _cache[3] || (_cache[3] = _withKeys(($event: any) => (_unref(onEnterKey) != null ? _unref(onEnterKey)() : undefined), ["enter"]))
    }, {
      prepend: _withCtx(() => [
        _createVNode(_component_q_icon, { name: "lock" })
      ]),
      append: _withCtx(() => [
        _createVNode(_component_q_btn, {
          flat: "",
          round: "",
          dense: "",
          icon: "cancel",
          class: "set-outline",
          color: "primary",
          title: computedResetLabel.value,
          disable: _unref(disable),
          onClick: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (clearPassword()), ["stop"]))
        }, null, 8 /* PROPS */, ["title", "disable"]),
        _createVNode(_component_q_btn, {
          flat: "",
          dense: "",
          round: "",
          icon: isPwd.value ? 'visibility_off' : 'visibility',
          class: "set-outline",
          color: "primary",
          title: isPwd.value ? computedShowLabel.value : computedHideLabel.value,
          "aria-pressed": !isPwd.value,
          disable: _unref(disable),
          onClick: _cache[1] || (_cache[1] = ($event: any) => (isPwd.value = !isPwd.value))
        }, null, 8 /* PROPS */, ["icon", "title", "aria-pressed", "disable"])
      ]),
      _: 1 /* STABLE */
    }, 8 /* PROPS */, ["model-value", "dense", "placeholder", "aria-required", "aria-describedby", "aria-invalid", "aria-disable", "disable", "label", "type", "bg-color", "label-color", "class", "input-class", "rules"])
  ]))
}
}

})